import { useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { submitContact } from "@/lib/contact.functions";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email").max(254),
  name: z.string().trim().max(120).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

type Variant = "inline" | "full";

interface Props {
  source: string;
  variant?: Variant;
  heading?: string;
  subheading?: string;
  showName?: boolean;
  showMessage?: boolean;
  buttonLabel?: string;
  successMessage?: string;
}

export function EmailCaptureForm({
  source,
  variant = "inline",
  heading,
  subheading,
  showName = false,
  showMessage = false,
  buttonLabel = "Get in touch",
  successMessage = "Thanks — I'll be in touch shortly.",
}: Props) {
  const submit = useServerFn(submitContact);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = schema.safeParse({ email, name, message });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }

    setStatus("submitting");
    try {
      await submit({
        data: {
          email: parsed.data.email,
          name: parsed.data.name || "",
          message: parsed.data.message || "",
          source,
        },
      });
    } catch (err) {
      console.error("email capture submit failed", err);
      setStatus("error");
      setError("Something went wrong. Please email katie@katiefernands.com directly.");
      return;
    }

    setStatus("success");
    setEmail("");
    setName("");
    setMessage("");
  }

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 text-foreground">
        <p className="font-medium">{successMessage}</p>
      </div>
    );
  }

  const wrapperClass =
    variant === "full"
      ? ""
      : "rounded-2xl border border-border/60 bg-muted p-6 md:p-8";

  return (
    <div className={wrapperClass}>
      {heading && (
        <h3 className="text-xl md:text-2xl font-serif tracking-tight text-foreground">
          {heading}
        </h3>
      )}
      {subheading && <p className="mt-2 text-foreground/70">{subheading}</p>}

      <form onSubmit={onSubmit} className={`${heading || subheading ? "mt-5" : ""} space-y-3`}>
        {showName && (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={120}
            className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm text-foreground placeholder:text-foreground/40 focus:border-primary focus:outline-none"
          />
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@firm.com"
            maxLength={254}
            className="flex-1 rounded-full border border-border/60 bg-background px-5 py-3 text-sm text-foreground placeholder:text-foreground/40 focus:border-primary focus:outline-none"
          />
          {!showMessage && (
            <button
              type="submit"
              disabled={status === "submitting"}
              className="rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {status === "submitting" ? "Sending…" : buttonLabel}
            </button>
          )}
        </div>
        {showMessage && (
          <>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's the situation? (optional)"
              maxLength={2000}
              rows={4}
              className="w-full rounded-2xl border border-border/60 bg-background px-5 py-3 text-sm text-foreground placeholder:text-foreground/40 focus:border-primary focus:outline-none"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {status === "submitting" ? "Sending…" : buttonLabel}
            </button>
          </>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <p className="text-xs text-foreground/50">
          No spam. Just a reply from me.
        </p>
      </form>
    </div>
  );
}
